import Phaser from 'phaser'

import {
  type AgentRole,
  type AgentStyle,
  CAPE_PATH,
  CHARACTERS,
  EYE_RING,
  GLOW,
  HAT_EDGE,
  type PixelRect,
  type RigShape,
  agentBodyRects,
  agentLookColours,
  badgeOf,
  badgeShapes,
  capeFill,
  capeOf,
  characterOf,
  eyeColour,
  eyeRings,
  hatShapes,
  visorFace,
} from '~/game/agentCharacters'

/**
 * An agent's look on the floor, drawn from the shared rig (agentCharacters.ts)
 * the way the Dashboard's AgentSprite composes it: cape behind, body, eye
 * rings, eyes or visor, hat, badge. Each distinct look + state is one SVG, loaded once per
 * game as a texture that every agent wearing it shares; the role's emblem is
 * its own small texture (see Character: it sits by the nameplate, which the
 * desk never covers).
 */
export interface AgentLook extends AgentRole {
  /** Its stored look. Null = the classic pixel robot in its role's colour, as before. */
  style: AgentStyle | null
  /** Readiness UNPLUGGED (it cannot work until a seat, plan or engine is fixed):
   *  greyed, eyes shut, a loose plug. */
  unplugged: boolean
}

/**
 * Texture pixels per rig unit. The sprite shows 2.2 screen pixels a unit at
 * zoom 1 and the floor zooms to 2.2, so ~4.8 at most: 5 stays crisp there.
 */
export const AGENT_TEXTURE_RES = 5

/** Rig units of margin on every side of the 20x20 grid (hats rise above it,
 *  the plug trails right). Equal on all sides, so the grid stays centred and
 *  the sprite sits exactly where the 20x20 robot did. */
const PAD = 4
const SPAN = 20 + PAD * 2

/** The emblem's size in floor pixels, as the Dashboard draws it on a 44px sprite. */
export const EMBLEM_SIZE = 16
const EMBLEM_RES = 4

const HEX = /^#[0-9a-f]{6}$/i
const own = (catalog: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(catalog, key)

/** The stored look with anything outside the catalogs read as its default,
 *  so only known shapes and #RRGGBB colours ever reach an SVG. */
function normalStyle(style: AgentStyle | null): AgentStyle | null {
  if (!style) return null
  const character = String(style.character ?? '')
  const hat = String(style.hat ?? '')
  // A style this copy does not know reads as the boolean (red cape, star).
  const cape = capeOf({ cape: style.cape === true, capeStyle: style.capeStyle })
  const badge = badgeOf({ badge: style.badge === true, badgeStyle: style.badgeStyle })
  return {
    character: own(CHARACTERS, character) ? character : 'robot',
    color: typeof style.color === 'string' && HEX.test(style.color) ? style.color.toLowerCase() : null,
    hat: hatShapes(hat, 0).length ? hat : 'none',
    cape: cape !== 'none',
    badge: badge !== 'none',
    capeStyle: cape,
    badgeStyle: badge,
  }
}

/** The body colour: the look's own, else its role's (teal PA, coral EA, indigo). */
const bodyColour = (look: AgentLook, style: AgentStyle): string =>
  style.color ?? agentLookColours(look).body

/**
 * The texture that draws this look. Everything that changes a pixel is in the
 * key, so agents dressed alike share one texture and a change is a new key.
 */
export function agentTextureKey(look: AgentLook): string {
  const style = normalStyle(look.style)
  const state = look.unplugged ? 'unplugged' : 'awake'
  const parts = style
    ? [
        style.character,
        bodyColour(look, style).slice(1).toLowerCase(),
        style.hat,
        style.capeStyle !== 'none' ? `cape-${style.capeStyle}` : '',
        style.badgeStyle !== 'none' ? `badge-${style.badgeStyle}` : '',
      ]
    : [
        'classic',
        look.personal ? 'personal' : look.ea ? 'ea' : 'staff',
        look.hr ? 'hr' : '',
        look.steward ? (look.stewardStaff ? 'steward-staff' : 'steward') : '',
      ]
  return ['go-agent', ...parts, state].filter(Boolean).join('-')
}

/** The Dashboard's unplugged filter (grayscale(1) brightness(1.2)) on one colour. */
function greyed(hex: string): string {
  const n = hex.replace('#', '')
  if (n.length !== 6) return hex
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16))
  const v = Math.min(255, Math.round((0.2126 * r + 0.7152 * g + 0.0722 * b) * 1.2))
  const channel = v.toString(16).padStart(2, '0')
  return `#${channel}${channel}${channel}`
}

const num = (v: number | undefined): string => String(Math.round((v ?? 0) * 1000) / 1000)

/** One SVG of this look on the rig's grid, sized AGENT_TEXTURE_RES pixels a unit. */
export function agentSvg(look: AgentLook): string {
  const px = SPAN * AGENT_TEXTURE_RES
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" viewBox="${-PAD} ${-PAD} ${SPAN} ${SPAN}">${agentParts(look, true)}</svg>`
}

/** The look's shapes on the 20x20 grid, in paint order; the plug only where there is room for it. */
function agentParts(look: AgentLook, plug: boolean): string {
  const style = normalStyle(look.style)
  const paint = (fill: string): string => (look.unplugged ? greyed(fill) : fill)
  const character = characterOf(style)
  const pixelRobot = !style || !character.path
  const colour = style ? bodyColour(look, style) : undefined
  const rect = (r: PixelRect): string =>
    `<rect x="${num(r.x)}" y="${num(r.y)}" width="${num(r.w)}" height="${num(r.h)}" fill="${paint(r.fill)}"/>`
  // One hat or badge part: hats take the shared light edge, badges their own.
  const shape = (part: RigShape, edge: string): string => {
    const fill = paint(part.fill)
    if (part.kind === 'path') return `<path d="${part.d}" fill="${fill}" ${edge}/>`
    if (part.kind === 'circle') {
      return `<circle cx="${num(part.x)}" cy="${num(part.y)}" r="${num(part.r)}" fill="${fill}" ${edge}/>`
    }
    return `<rect x="${num(part.x)}" y="${num(part.y)}" width="${num(part.w)}" height="${num(part.h)}" rx="${num(part.rx)}" fill="${fill}" ${edge}/>`
  }
  const parts: string[] = []

  // The cape hangs behind the body, in its colour.
  const cape = style ? capeFill(style) : null
  if (cape) {
    parts.push(`<path d="${CAPE_PATH}" fill="${paint(cape)}" opacity="0.95"/>`)
  }

  // The body: the classic robot's pixels (in its role's look when unstyled,
  // in the chosen colour when styled), or the character's shape.
  if (pixelRobot) {
    const rects = agentBodyRects(style ? {} : look, colour)
    parts.push(`<g shape-rendering="crispEdges">${rects.map(rect).join('')}</g>`)
  } else {
    const fill = paint(colour ?? '#5b54c8')
    const outline = character.rounded
      ? ` stroke="${fill}" stroke-width="1.25" stroke-linejoin="round"`
      : ''
    parts.push(`<path d="${character.path}" fill="${fill}"${outline}/>`)
    // A thin body (the paper clip): a light ring behind each eye.
    if (character.eyeRing) {
      for (const ring of eyeRings(character.eyesY)) {
        parts.push(
          `<circle cx="${num(ring.x)}" cy="${num(ring.y)}" r="${EYE_RING.r}" fill="${EYE_RING.fill}" stroke="${EYE_RING.stroke}" stroke-width="${EYE_RING.strokeWidth}"/>`,
        )
      }
    }
  }

  // The eyes: the robot's glow, the cute robot's visor, or the shared eyes.
  // Unplugged they are shut and faint (the Dashboard flickers them; a still
  // texture keeps them just visible).
  const eyes: string[] = []
  if (pixelRobot) {
    eyes.push(rect(GLOW))
  } else if (character.visor) {
    for (const part of visorFace(character.eyesY, look.unplugged ? 'shut' : 'open')) {
      eyes.push(
        `<rect x="${num(part.x)}" y="${num(part.y)}" width="${num(part.w)}" height="${num(part.h)}" rx="${num(part.rx)}" fill="${paint(part.fill)}"/>`,
      )
    }
  } else if (look.unplugged) {
    for (const x of [6.8, 10.8]) {
      eyes.push(
        `<rect x="${x}" y="${num(character.eyesY)}" width="2.4" height="0.7" rx="0.35" fill="${eyeColour(character)}"/>`,
      )
    }
  } else {
    const y = character.eyesY
    const fill = eyeColour(character)
    eyes.push(
      `<rect x="7.4" y="${num(y - 1.5)}" width="1.2" height="2.5" rx="0.6" fill="${fill}" transform="rotate(-12 8 ${num(y)})"/>`,
      `<rect x="11.4" y="${num(y - 1.5)}" width="1.2" height="2.5" rx="0.6" fill="${fill}" transform="rotate(12 12 ${num(y)})"/>`,
    )
  }
  parts.push(`<g opacity="${look.unplugged ? 0.3 : 1}">${eyes.join('')}</g>`)

  // The extras: a hat on the head top, the badge pinned on the chest.
  if (style) {
    const edge = `stroke="${HAT_EDGE.stroke}" stroke-opacity="${HAT_EDGE.strokeOpacity}" stroke-width="${HAT_EDGE.strokeWidth}"`
    for (const part of hatShapes(style.hat, character.top)) parts.push(shape(part, edge))
    for (const part of badgeShapes(badgeOf(style), character.chest)) {
      parts.push(shape(part, part.stroke ? `stroke="${part.stroke}" stroke-width="${num(part.strokeWidth)}"` : ''))
    }
  }

  // Unplugged: the loose plug trailing off to the right.
  if (look.unplugged && plug) {
    parts.push(
      '<path d="M15 16 C18 18 18.5 20 21.5 20" fill="none" stroke="#9a9aab" stroke-linecap="round" stroke-width="0.8"/>',
      '<rect x="21" y="18.7" width="2" height="2.6" rx="0.4" fill="#9a9aab"/>',
      '<rect x="23" y="19.1" width="0.9" height="0.5" fill="#9a9aab"/>',
      '<rect x="23" y="20.3" width="0.9" height="0.5" fill="#9a9aab"/>',
    )
  }

  return parts.join('')
}

export interface Emblem {
  text: string
  fill: string
}

export const emblemTextureKey = (emblem: Emblem): string =>
  `go-emblem-${emblem.text === '★' ? 'star' : emblem.text}-${emblem.fill.slice(1).toLowerCase()}`

/** A disc in the emblem's colour with a white ring and its bold white letters (or star). */
function emblemParts(emblem: Emblem, cx: number, cy: number, d: number, ring: number, fontSize: number): string {
  return (
    `<circle cx="${num(cx)}" cy="${num(cy)}" r="${num(d / 2 - ring / 2)}" fill="${emblem.fill}" stroke="#ffffff" stroke-width="${num(ring)}"/>` +
    `<text x="${num(cx)}" y="${num(cy + d * 0.02)}" text-anchor="middle" dominant-baseline="central" fill="#ffffff" ` +
    `font-family="system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif" font-weight="800" font-size="${num(fontSize)}">` +
    `${emblem.text}</text>`
  )
}

/** The role's emblem as the Dashboard draws it: a filled disc, a white ring, bold white letters. */
export function emblemSvg(emblem: Emblem): string {
  const size = EMBLEM_SIZE
  const px = size * EMBLEM_RES
  // The Dashboard's type sizes on a 44px sprite: letters 15% of it, the star 22%.
  const fontSize = emblem.text.length > 1 ? 6.6 : 9.7
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" viewBox="0 0 ${size} ${size}">` +
    `${emblemParts(emblem, size / 2, size / 2, size, 1.2, fontSize)}</svg>`
  )
}

// The avatar frame: the 20x20 grid with a margin for hats and the emblem's
// overhang, equal on every side so the character stays centred.
const AVATAR_PAD = 1.5
const AVATAR_SPAN = 20 + AVATAR_PAD * 2
const avatarUrls = new Map<string, string>()

/**
 * The agent's character as a still avatar for the panels (an AI agent with
 * no uploaded picture wears its character, Malek 30 Sep): the look and the
 * role's emblem in the corner, as the Dashboard's sprite draws them, with no
 * disc behind. `size` is the avatar's box in CSS pixels; the emblem shows
 * from 20 px (Dashboard rule), its disc and letters kept to the Dashboard's
 * minimums. Unplugged reads grey with its eyes shut (no plug: no room).
 * One data URL per look, state and size.
 */
export function agentAvatarUrl(look: AgentLook, emblem: Emblem | null, size: number): string {
  const px = Math.max(8, Math.round(size))
  const shown = px >= 20 ? emblem : null
  const key = `${agentTextureKey(look)}|${shown ? emblemTextureKey(shown) : ''}|${px}`
  const cached = avatarUrls.get(key)
  if (cached) return cached
  let badge = ''
  if (shown) {
    // The Dashboard's emblem on a sprite of S px: max(11, 0.36 S) across,
    // right and bottom edges 0.07 S and 0.05 S past the sprite, ring 1.2 px,
    // letters max(6, 0.15 S) (the star 0.22 S). Here S is the grid's share
    // of the box; everything converted to grid units.
    const unit = px / AVATAR_SPAN
    const sprite = 20 * unit
    const d = Math.max(11, Math.round(sprite * 0.36)) / unit
    const right = Math.min(20 + 0.07 * 20, 20 + AVATAR_PAD)
    const bottom = Math.min(20 + 0.05 * 20, 20 + AVATAR_PAD)
    const font = Math.max(6, sprite * (shown.text.length > 1 ? 0.15 : 0.22)) / unit
    badge = emblemParts(shown, right - d / 2, bottom - d / 2, d, 1.2 / unit, font)
  }
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${px * 2}" height="${px * 2}" ` +
    `viewBox="${-AVATAR_PAD} ${-AVATAR_PAD} ${AVATAR_SPAN} ${AVATAR_SPAN}">${agentParts(look, false)}${badge}</svg>`
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
  avatarUrls.set(key, url)
  return url
}

/** The emblem texture's own scale: EMBLEM_SIZE floor pixels. */
export const EMBLEM_SCALE = 1 / EMBLEM_RES

// Keys whose image is decoding, per game (textures outlive scene restarts).
const decoding = new WeakMap<Phaser.Textures.TextureManager, Set<string>>()

/**
 * Run `ready` once the texture `key` exists, making it from the SVG the first
 * time it is asked for. An SVG decodes asynchronously, so a new look shows a
 * frame or so after it is asked for; an existing one runs `ready` at once.
 * The texture is smoothed (the game is pixelArt, which would alias a vector
 * shape drawn at a fraction of its size).
 */
export function whenSvgTexture(
  scene: Phaser.Scene,
  key: string,
  svg: () => string,
  ready: () => void,
): void {
  const textures = scene.textures
  if (textures.exists(key)) {
    ready()
    return
  }
  let pending = decoding.get(textures)
  if (!pending) {
    pending = new Set()
    decoding.set(textures, pending)
  }
  if (!pending.has(key)) {
    pending.add(key)
    const done = pending
    // Registered before any caller's `ready`, so the filter is set first.
    textures.once(
      Phaser.Textures.Events.ADD_KEY + key,
      (texture: Phaser.Textures.Texture) => {
        done.delete(key)
        texture.setFilter(Phaser.Textures.FilterMode.LINEAR)
      },
    )
    const failed = (failedKey: string): void => {
      if (failedKey !== key) return
      textures.off(Phaser.Textures.Events.ERROR, failed)
      done.delete(key)
      console.warn(`[glass-office] could not draw ${key}`)
    }
    textures.on(Phaser.Textures.Events.ERROR, failed)
    textures.once(Phaser.Textures.Events.ADD_KEY + key, () =>
      textures.off(Phaser.Textures.Events.ERROR, failed),
    )
    textures.addBase64(key, `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg())}`)
  }
  textures.once(Phaser.Textures.Events.ADD_KEY + key, ready)
}
